/**
 * useProfileChange — BACKLOG-1675 (Bill 2026-10-02); no deferral, CHG-0186 (Bill 2026-11-04).
 *
 * Runs the existing-customer profile-change ceremony at a save choke point:
 *
 *   begin(person, ctx)  →  preview against live Core Director
 *                       →  no change? resolve straight through
 *                       →  change? open ProfileChangeConfirmModal and await the officer
 *                       →  Confirm: apply (CustMod + waterfall + notes + insider scan), stamp the
 *                          person with the applied key, resolve
 *                       →  Discard (application surface): Core's current values go back on the
 *                          person for every changed field; resolve discarded=true
 *                       →  Cancel (CIF surface): resolve with applied=false
 *
 * Bill 2026-11-04: "This is contact information that is either correct at the moment of collection or
 * not. If we trust it from the customer it should be updated, why delay the update …" On an
 * application in confirm mode there is no deferral: the change is written to Core now or it is not
 * kept. When Core cannot be reached to compare, a dialog says so with Try again / Discard change —
 * the edit is never saved as changed and never queued for the commit. A refusal at apply keeps the
 * confirm dialog open with the reason (Confirm is the retry). profileChange.mode = commit-time (the
 * admin-configurable alternative) skips the ceremony and the commit writes the change, as before.
 *
 * `begin` returns a promise so the caller can `await` the officer's decision
 * inline: `const o = await begin(saved, {...}); if (!o.proceed) return; save(o.person)`.
 * Render `modal` once in the host's tree.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import type {
  ProfileChangeApplyResponse,
  ProfileChangePreviewResponse,
  ProfileChangeSource,
} from '@retail-ops/shared-types';
import { Banner, Button, ModalShell } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useConfig } from '@/contexts/ConfigContext';
import type { RaoPerson } from '@/modules/RAO/types';
import { ProfileChangeConfirmModal, type ProfileChangeConfirmParams } from './ProfileChangeConfirmModal';
import {
  discardNotice,
  personToProfileChangeInput,
  readProfileChangeMode,
  revertToCore,
  stampProfileChange,
  summarizeApply,
} from './profileChange';

export interface ProfileChangeNotice {
  tone: 'ok' | 'warn' | 'err';
  text: string;
}

export interface ProfileChangeOutcome {
  /** False when nothing may be saved (Core unreachable and the officer discarded, or a refusal). */
  proceed: boolean;
  /** The person: stamped with the applied key when Core was written; Core's values back on a discard. */
  person: RaoPerson;
  applied: boolean;
  /** True when the officer discarded the change and `person` carries Core's current values. */
  discarded?: boolean;
  notice?: ProfileChangeNotice;
}

export interface ProfileChangeContext {
  source: ProfileChangeSource;
  cartId?: string;
  /** cart: edits also live on an application; cif: standalone CIF edit. */
  surface: 'cart' | 'cif';
}

interface ModalState {
  person: RaoPerson;
  cifId: string;
  preview: ProfileChangePreviewResponse;
  ctx: ProfileChangeContext;
}

interface UnreachableState {
  person: RaoPerson;
  cifId: string;
  ctx: ProfileChangeContext;
  message: string;
}

export interface UseProfileChangeResult {
  begin: (person: RaoPerson, ctx: ProfileChangeContext) => Promise<ProfileChangeOutcome>;
  /** True while the preview request is in flight (before the modal opens). */
  comparing: boolean;
  modal: JSX.Element | null;
}

/** The application-surface message when the change was not kept because Core could not be compared. */
export const NOT_SAVED_UNREACHABLE =
  'Change not saved. Core Director could not be reached to compare this customer, so nothing was written and the application keeps its previous values.';

type PreviewResult =
  | { kind: 'ok'; preview: ProfileChangePreviewResponse }
  /** thrown = the request itself failed (vs Core answering without the customer). */
  | { kind: 'unreachable'; message: string; thrown: boolean }
  | { kind: 'refused'; outcome: Omit<ProfileChangeOutcome, 'person'> };

export function useProfileChange(): UseProfileChangeResult {
  const client = useApiClient();
  const { config } = useConfig();
  const [state, setState] = useState<ModalState | null>(null);
  const [unreachable, setUnreachable] = useState<UnreachableState | null>(null);
  const [comparing, setComparing] = useState<boolean>(false);
  const [applying, setApplying] = useState<boolean>(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [refreshedNotice, setRefreshedNotice] = useState<string | null>(null);
  const resolverRef = useRef<((o: ProfileChangeOutcome) => void) | null>(null);

  const mode = readProfileChangeMode(config.systemSettings);

  const finish = useCallback((outcome: ProfileChangeOutcome): void => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    setUnreachable(null);
    setApplying(false);
    setApplyError(null);
    setRefreshedNotice(null);
    resolve?.(outcome);
  }, []);

  const runPreview = useCallback(
    async (person: RaoPerson, cifId: string, ctx: ProfileChangeContext): Promise<PreviewResult> => {
      setComparing(true);
      try {
        const preview = await client.retail.profileChange.preview(cifId, {
          person: personToProfileChangeInput(person),
          source: ctx.source,
          ...(ctx.cartId ? { cartId: ctx.cartId } : {}),
        });
        if (preview.success) return { kind: 'ok', preview };
        if (preview.errorCode === 'CIF_NOT_FOUND') {
          return { kind: 'refused', outcome: { proceed: false, applied: false,
            notice: { tone: 'err', text: `Change not saved. ${preview.error ?? 'Core Director did not return this customer.'}` } } };
        }
        return { kind: 'unreachable', thrown: false, message: preview.error ?? 'Core Director did not return this customer, so the change could not be compared.' };
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        // Test gate (Bill 2026-10-17): the server refuses a real customer while maint.testCustomersOnly is on. Say so
        // plainly — it is not an outage.
        if ((err as { code?: string } | null)?.code === 'MAINT_TEST_CUSTOMERS_ONLY') {
          return { kind: 'refused', outcome: { proceed: false, applied: false, notice: { tone: 'err', text: msg } } };
        }
        return { kind: 'unreachable', thrown: true, message: `Core Director could not be reached to compare this customer's record (${msg}).` };
      } finally {
        setComparing(false);
      }
    },
    [client],
  );

  /** Route a preview result: no change → resolve; change → confirm modal; unreachable → retry dialog. */
  const route = useCallback(
    (r: PreviewResult, person: RaoPerson, cifId: string, ctx: ProfileChangeContext): void => {
      if (r.kind === 'refused') { finish({ ...r.outcome, person }); return; }
      if (r.kind === 'unreachable') {
        if (ctx.surface === 'cif') {
          // Customer Management keeps its existing behaviour (unchanged by CHG-0186).
          finish({ proceed: r.thrown, person, applied: false,
            notice: { tone: 'warn', text: r.thrown ? `${r.message} Nothing was written.` : r.message } });
          return;
        }
        setState(null);
        setUnreachable({ person, cifId, ctx, message: r.message });
        return;
      }
      if (!r.preview.hasChanges) { finish({ proceed: true, person, applied: false }); return; }
      setUnreachable(null);
      setApplyError(null);
      setRefreshedNotice(null);
      setState({ person, cifId, preview: r.preview, ctx });
    },
    [finish],
  );

  const begin = useCallback(
    async (person: RaoPerson, ctx: ProfileChangeContext): Promise<ProfileChangeOutcome> => {
      const cifId = (person.cifId ?? '').trim();
      if (!cifId || mode !== 'confirm') return { proceed: true, person, applied: false };
      return new Promise<ProfileChangeOutcome>((resolve) => {
        resolverRef.current = resolve;
        void runPreview(person, cifId, ctx).then((r) => route(r, person, cifId, ctx));
      });
    },
    [mode, runPreview, route],
  );

  const onRetry = useCallback((): void => {
    if (!unreachable) return;
    const { person, cifId, ctx } = unreachable;
    void runPreview(person, cifId, ctx).then((r) => {
      if (r.kind === 'unreachable') { setUnreachable({ person, cifId, ctx, message: r.message }); return; }
      route(r, person, cifId, ctx);
    });
  }, [unreachable, runPreview, route]);

  const onDiscardUnreachable = useCallback((): void => {
    if (!unreachable) return;
    finish({ proceed: false, person: unreachable.person, applied: false, notice: { tone: 'err', text: NOT_SAVED_UNREACHABLE } });
  }, [unreachable, finish]);

  const onCancel = useCallback((): void => {
    if (!state) return;
    if (state.ctx.surface === 'cart') {
      // Discard: Core's current values go back on the application for every changed field.
      finish({
        proceed: true,
        person: revertToCore(state.person, state.preview.diff, state.preview.current),
        applied: false,
        discarded: true,
        notice: discardNotice(state.preview.diff),
      });
      return;
    }
    finish({ proceed: true, person: state.person, applied: false, notice: { tone: 'warn', text: 'No changes were sent to Core Director.' } });
  }, [state, finish]);

  const onConfirm = useCallback(
    async (params: ProfileChangeConfirmParams): Promise<void> => {
      if (!state) return;
      setApplying(true);
      setApplyError(null);
      try {
        // A 409 PREVIEW_STALE arrives as a thrown ApiError (code = the body's errorCode); treat it like the response
        // below so the comparison is refreshed instead of showing a raw "Not saved" (Bill 2026-10-17 support report).
        const resp: ProfileChangeApplyResponse = await client.retail.profileChange.apply(state.cifId, {
          person: personToProfileChangeInput(state.person),
          authMethodCode: params.authMethodCode,
          customerInitials: params.customerInitials,
          accounts: params.accounts.map((a) => ({ acctId: a.acctId, acctType: a.acctType })),
          previewToken: state.preview.previewToken,
          source: state.ctx.source,
          ...(state.ctx.cartId ? { cartId: state.ctx.cartId } : {}),
        }).catch((e: unknown) => {
          if ((e as { code?: string } | null)?.code === 'PREVIEW_STALE') {
            return { success: false, errorCode: 'PREVIEW_STALE', error: e instanceof Error ? e.message : String(e) } as unknown as ProfileChangeApplyResponse;
          }
          throw e;
        });

        if (!resp.success) {
          if (resp.errorCode === 'PREVIEW_STALE') {
            // Someone changed the record meanwhile — re-compare and show it again.
            try {
              const fresh = await client.retail.profileChange.preview(state.cifId, {
                person: personToProfileChangeInput(state.person),
                source: state.ctx.source,
                ...(state.ctx.cartId ? { cartId: state.ctx.cartId } : {}),
              });
              if (fresh.success && fresh.hasChanges) {
                setState({ ...state, preview: fresh });
                setRefreshedNotice('Core Director\'s record changed since this review opened. The comparison was refreshed — check it again before confirming.');
                setApplying(false);
                return;
              }
              if (fresh.success && !fresh.hasChanges) {
                finish({ proceed: true, person: state.person, applied: false,
                  notice: { tone: 'ok', text: 'Core Director already holds these values; nothing needed to change.' } });
                return;
              }
            } catch { /* fall through to the error banner */ }
          }
          // Stays open with the reason: Confirm tries again, Discard puts Core's values back.
          setApplyError(resp.error ?? 'Core Director did not accept the change.');
          setApplying(false);
          return;
        }

        const stamped = resp.applied ? stampProfileChange(state.person, resp, params.authMethodCode) : state.person;
        const notice: ProfileChangeNotice = resp.applied
          ? summarizeApply(resp)
          : { tone: 'warn', text: resp.warnings[0] ?? 'Nothing was written to Core Director.' };
        finish({ proceed: true, person: stamped, applied: resp.applied, notice });
      } catch (err) {
        setApplyError(err instanceof Error ? err.message : String(err));
        setApplying(false);
      }
    },
    [state, client, finish],
  );

  const modal = useMemo((): JSX.Element | null => {
    if (unreachable) {
      return (
        <ModalShell
          open
          onClose={onDiscardUnreachable}
          title="Core Director could not be reached"
          size="md"
          disableBackdropClose
          footer={
            <>
              <Button variant="ghost" onClick={onDiscardUnreachable} disabled={comparing}>Discard change</Button>
              <Button variant="primary" onClick={onRetry} disabled={comparing}>
                {comparing ? 'Comparing with Core Director…' : 'Try again'}
              </Button>
            </>
          }
        >
          <Banner tone="err" title="The change is not saved yet">
            {unreachable.message} A change to an existing customer is written to Core Director when it is made, or not
            kept. Try again, or discard the change to keep what the application had.
          </Banner>
        </ModalShell>
      );
    }
    if (!state) return null;
    const name = `${state.person.firstName ?? ''} ${state.person.lastName ?? ''}`.trim();
    return (
      <ProfileChangeConfirmModal
        open
        cifId={state.cifId}
        {...(name ? { customerName: name } : {})}
        preview={state.preview}
        context={state.ctx.surface}
        applying={applying}
        applyError={applyError}
        refreshedNotice={refreshedNotice}
        onCancel={onCancel}
        onConfirm={onConfirm}
      />
    );
  }, [unreachable, state, applying, applyError, refreshedNotice, comparing, onCancel, onConfirm, onRetry, onDiscardUnreachable]);

  return { begin, comparing, modal };
}
